/**
 * AXOIX Chat Widget
 * Connected to the AXOIX Mothership API
 * Features: Real AI responses, Speech Recognition, Text-to-Speech, Site Navigation
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import "./AxoixChat.css";
import { fetchPricingData, PricingTierAPI } from "../../services/api/pricingService";

// AXOIX Avatar
import axoixAvatar from "../../images/team/chat_widget.webp";

// =================================================================
// SITE NAVIGATION MAPPINGS
// =================================================================

interface SiteRoute {
  path: string;
  name: string;
  description: string;
  keywords: string[];
  section?: string; // For anchor links on homepage
  icon?: string;
}

// Complete site map for AXOIX navigation
const SITE_NAVIGATION: SiteRoute[] = [
  // Main Pages
  {
    path: "/",
    name: "Home",
    description: "AXOIX homepage - intelligent Business Operating System",
    keywords: ["home", "main", "start", "homepage", "landing", "welcome"],
    icon: "🏠"
  },
  {
    path: "/about",
    name: "About Us",
    description: "Learn about AXOIX, our mission and vision",
    keywords: ["about", "mission", "vision", "company", "who", "story", "founder"],
    icon: "ℹ️"
  },
  {
    path: "/features",
    name: "Features",
    description: "All AXOIX features, services, and capabilities",
    keywords: ["feature", "service", "capability", "function", "what can", "tools", "seal", "chorus", "remedy", "costing"],
    icon: "⚡"
  },
  {
    path: "/team",
    name: "AI Characters",
    description: "Meet all 22 AI characters - HYPERION, VARN, LINK, FORGE, LEDGER and more",
    keywords: ["team", "character", "ai", "hyperion", "varn", "link", "forge", "ledger", "keeper", "haul", "cipher", "barter", "voyage", "echo", "chorus", "crew"],
    icon: "🤖"
  },
  {
    path: "/pricing",
    name: "Pricing",
    description: "View all plans - Explorer (Free), Officer, Commander, Titan",
    keywords: ["price", "pricing", "cost", "plan", "subscription", "buy", "purchase", "explorer", "officer", "commander", "titan", "free", "rupee", "₹"],
    icon: "💰"
  },
  {
    path: "/contact",
    name: "Contact",
    description: "Get in touch with AXOIX team",
    keywords: ["contact", "email", "support", "help", "reach", "message", "inquiry", "question"],
    icon: "📧"
  },
  {
    path: "/signup",
    name: "Sign Up",
    description: "Create your AXOIX account",
    keywords: ["signup", "sign up", "register", "create account", "join", "get started", "trial", "start"],
    icon: "🚀"
  },
  {
    path: "/faq",
    name: "FAQ",
    description: "Frequently asked questions about AXOIX",
    keywords: ["faq", "question", "answer", "help", "how", "what", "why", "common"],
    icon: "❓"
  },

  // Partner Pages
  {
    path: "/become-partner",
    name: "Become a Partner",
    description: "Join the AXOIX Partner Program - earn recurring commission on every referral",
    keywords: ["partner", "affiliate", "referral", "commission", "earn", "reseller"],
    icon: "🤝"
  },
  {
    path: "/partner/login",
    name: "Partner Login",
    description: "Access your partner dashboard",
    keywords: ["partner login", "partner dashboard", "affiliate login"],
    icon: "🔐"
  },

  // Legal Pages
  {
    path: "/terms",
    name: "Terms & Conditions",
    description: "Terms of service for AXOIX",
    keywords: ["terms", "conditions", "tos", "service agreement", "legal"],
    icon: "📜"
  },
  {
    path: "/privacy",
    name: "Privacy Policy",
    description: "How we protect your data",
    keywords: ["privacy", "data", "protection", "gdpr", "personal"],
    icon: "🔒"
  },
  {
    path: "/refund",
    name: "Refund Policy",
    description: "7-day money-back guarantee details",
    keywords: ["refund", "money back", "return", "cancel", "guarantee"],
    icon: "💸"
  },
  {
    path: "/cookies",
    name: "Cookie Policy",
    description: "Cookie usage information",
    keywords: ["cookie", "cookies", "tracking"],
    icon: "🍪"
  },

  // Other Pages
  {
    path: "/careers",
    name: "Careers",
    description: "Join the AXOIX team",
    keywords: ["career", "job", "work", "hiring", "employment", "position"],
    icon: "💼"
  },
  {
    path: "/blog",
    name: "Blog",
    description: "Latest news and updates",
    keywords: ["blog", "news", "article", "update", "post"],
    icon: "📰"
  },

  // Homepage Sections (Anchor Links)
  {
    path: "/#hero",
    name: "Hero Section",
    description: "Top of homepage with main features",
    keywords: ["hero", "top", "main", "intro"],
    section: "hero",
    icon: "🎯"
  },
  {
    path: "/#about",
    name: "About Section",
    description: "About AXOIX on homepage",
    keywords: ["about section"],
    section: "about",
    icon: "ℹ️"
  },
  {
    path: "/#service",
    name: "Services Section",
    description: "All services on homepage",
    keywords: ["service section", "features section"],
    section: "service",
    icon: "⚡"
  },
  {
    path: "/#link",
    name: "LINK Phone System",
    description: "LINK business phone system section",
    keywords: ["link section", "phone section", "pbx", "voip", "calling"],
    section: "link",
    icon: "📞"
  },
  {
    path: "/#industries",
    name: "Industry Modules",
    description: "8 industry-specific modules",
    keywords: ["industry", "module", "hotel", "tourism", "restaurant", "education", "healthcare", "logistics", "real estate", "barter"],
    section: "industries",
    icon: "🏭"
  },
  {
    path: "/#team",
    name: "AI Team Section",
    description: "AI characters section on homepage",
    keywords: ["team section", "characters section"],
    section: "team",
    icon: "🤖"
  },
  {
    path: "/#pricing",
    name: "Pricing Section",
    description: "Pricing section on homepage",
    keywords: ["pricing section"],
    section: "pricing",
    icon: "💰"
  },
  {
    path: "/#faq",
    name: "FAQ Section",
    description: "FAQ section on homepage",
    keywords: ["faq section"],
    section: "faq",
    icon: "❓"
  },
  {
    path: "/#contact",
    name: "Contact Section",
    description: "Contact section on homepage",
    keywords: ["contact section"],
    section: "contact",
    icon: "📧"
  }
];

// =================================================================
// NUMBER TO WORDS FOR TTS
// Browser SpeechSynthesis misreads raw numbers (22→"two", 18→"8")
// =================================================================
function numberToWords(num: number): string {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
    'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  if (num === 0) return 'zero';
  if (num < 0) return 'minus ' + numberToWords(-num);
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 === 0 ? '' : '-' + ones[num % 10]);
  if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 === 0 ? '' : ' and ' + numberToWords(num % 100));
  if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' thousand' + (num % 1000 === 0 ? '' : ' ' + numberToWords(num % 1000));
  if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' lakh' + (num % 100000 === 0 ? '' : ' ' + numberToWords(num % 100000));
  return String(num);
}

function convertNumbersForSpeech(text: string): string {
  const digitNames = ['zero','one','two','three','four','five','six','seven','eight','nine'];
  function spellDigits(s: string): string {
    return s.replace(/\d/g, d => digitNames[parseInt(d)] + ' ').trim();
  }

  // Strip markdown bold/italic so "**Rs 4,999**" becomes "Rs 4,999"
  text = text.replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1');

  // 1. Phone numbers: +XX XXXXX XXXXX → digit by digit
  text = text.replace(/\+(\d[\d\s\-]{7,})/g, (_match, digits) => {
    return 'plus ' + spellDigits(digits);
  });

  // 2. Pin codes: standalone 6-digit numbers → digit by digit
  text = text.replace(/\b(\d{6})\b/g, (_match, pin) => {
    return 'pin code ' + spellDigits(pin);
  });

  // 3. Currency: "Rs 4,999" or "Rs. 14,999" or "₹4,999" → "rupees X"
  text = text.replace(/(?:Rs\.?|₹)\s*([\d,]+)/gi, (_match, numStr) => {
    const num = parseInt(numStr.replace(/,/g, ''), 10);
    if (!isNaN(num) && num <= 9999999) return 'rupees ' + numberToWords(num);
    return _match;
  });

  // 4. "24/7" → "twenty-four seven"
  text = text.replace(/\b24\/7\b/g, 'twenty-four seven');

  // 4b. Handle "/month", "/year", "/user", "/employee" etc. → "per month", "per year"
  text = text.replace(/\/(month|year|user|employee|company|day|week|hr|hour|min|minute|sec|second|annually|quarterly)/gi,
    (_match, unit) => ' per ' + unit);

  // 5. Comma-formatted numbers NOT preceded by Rs: "4,999" "14,999" "1,50,000"
  text = text.replace(/\b(\d{1,2}(?:,\d{2,3})+)\b/g, (_match, numStr) => {
    const num = parseInt(numStr.replace(/,/g, ''), 10);
    if (!isNaN(num) && num <= 9999999) return numberToWords(num);
    return _match;
  });

  // 6. Protect emails from digit conversion
  const emails: string[] = [];
  text = text.replace(/[\w.-]+@[\w.-]+\.\w+/g, (match) => {
    emails.push(match);
    return `__EMAIL${emails.length - 1}__`;
  });

  // 7. Remaining standalone numbers → words (22, 81, 32, 4999 etc.)
  text = text.replace(/\b(\d{1,5})\b/g, (match, numStr) => {
    const num = parseInt(numStr, 10);
    if (num >= 1900 && num <= 2099) return match; // Skip years
    if (num >= 0 && num <= 99999) return numberToWords(num);
    return match;
  });

  // Restore emails
  text = text.replace(/__EMAIL(\d+)__/g, (_m, idx) => emails[parseInt(idx)]);

  return text;
}

// Detect navigation intent from user message
function detectNavigationIntent(message: string): SiteRoute | null {
  const lowerMsg = message.toLowerCase();

  // EXCLUDE informational questions - these should NOT trigger navigation
  const infoQuestions = [
    "what is", "what are", "what does", "what's",
    "tell me about", "explain", "describe",
    "how does", "how do", "how is", "how can",
    "why is", "why does", "why do",
    "can you tell", "can you explain",
    "i want to know", "i need to know",
    "what do you", "who is", "who are"
  ];

  const isInfoQuestion = infoQuestions.some(q => lowerMsg.includes(q));
  if (isInfoQuestion) {
    return null; // Don't navigate for informational questions
  }

  // Navigation trigger phrases - MUST have one of these
  const navTriggers = [
    "take me to", "go to", "navigate to", "open the", "open ",
    "visit the", "visit ",
    "show me the", "show me ", "show the ",
    "i want to see the", "i want to see ",
    "bring me to", "redirect to", "switch to", "move to",
    "see the ", "see your ", "view the ", "view your ",
    "check out the", "check out your", "check the ",
    "where is the", "where is your", "where are the",
    "find the ", "find your "
  ];

  const hasNavIntent = navTriggers.some(trigger => lowerMsg.includes(trigger));

  // Find best matching route regardless of trigger phrase — a bare "pricing"
  // or "contact page" carries no verb but is still a navigation request.
  let bestMatch: SiteRoute | null = null;
  let bestScore = 0;

  for (const route of SITE_NAVIGATION) {
    let score = 0;

    // Check keyword matches
    for (const keyword of route.keywords) {
      if (lowerMsg.includes(keyword)) {
        score += keyword.length; // Longer matches = higher score
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = route;
    }
  }

  if (hasNavIntent) {
    // Explicit trigger phrase ("go to", "show me"...) lowers the bar — the
    // verb alone already signals intent, the keyword just picks the route.
    return bestScore >= 3 ? bestMatch : null;
  }

  // No trigger phrase: only act on a short, unambiguous message ("pricing",
  // "signup page") so this never hijacks a real sentence that happens to
  // contain a route keyword ("I want a demo of your pricing model" stays chat).
  const wordCount = lowerMsg.trim().split(/\s+/).length;
  if (wordCount <= 4 && bestScore >= 5) {
    return bestMatch;
  }

  return null;
}


// API Configuration - AXOIX Mothership API
const API_BASE = "https://api.axoix.in";

// The live model is ON as of 2026-08-12. See BR-009, and BR-028 for the fix.
//
// Unmatched questions go through /hyperion/* — the same-origin nginx proxy on
// axoix.com that injects the key from Vault, which is why AXOIX_CHAT_URL below
// uses that path rather than api.axoix.in directly. Keeping the proxy is
// deliberate even though BR-028 offers a direct call: the marketing key stays
// server-side instead of shipping in a public JS bundle.
//
// It was off because the answers were wrong, not because the transport was.
// A 3B model was free-associating below a similarity cutoff and its ungrounded
// prior was travel — "What is AXOIX?" returned "a B2B travel distribution
// platform", and a different invented company on each call. The mothership
// fixed the root cause by giving our own sites a `platform` caller type instead
// of making them masquerade as a tenant (BR-028), which puts them on the right
// RAG knowledge base.
//
// Re-verified from this repo before flipping — six live calls against the
// production proxy on 2026-08-12, not read from a changelog:
//   "What is AXOIX?"                 x3 → "an AI-assisted business operating
//                                    system (iBOS) for Indian SMBs", stable
//   "What does AXOIX do?"            → correct, no travel
//   "Is AXOIX a registered company?" → correct, quotes the registration doc
//   "Do you do flight bookings?"     → declines
//   "Do you sell insurance?"         → declines, offers a human handoff
//
// If this ever needs turning off again, verify the same way rather than
// trusting this comment.
// Typed as boolean, not inferred as the literal `true` — otherwise TypeScript
// narrows it and the offline guard below reads as unreachable code.
const LIVE_MODEL_ENABLED: boolean = true;

const AXOIX_STATUS_URL = `${API_BASE}/api/v1/hyperion/status`;
const AXOIX_CHAT_URL = "/hyperion/chat";

// ============ LEAD CAPTURE / VISITOR (ported from hyperion-widget.js) ============
// Project API key for the Mothership /visitors endpoint (issued by admin.axoix.in —
// see the mothership integration notes). Without it, the lead is kept locally and
// sync is skipped gracefully.
const HYPERION_API_KEY = process.env.REACT_APP_HYPERION_API_KEY || "";
const VISITOR_KEY = "axoix_visitor";

interface Visitor {
  id: string;
  name?: string;
  email?: string;
  mobile?: string;
  canContact?: boolean;
  visitCount: number;
  leadSent?: boolean;
}

function loadVisitor(): Visitor {
  try {
    const s = localStorage.getItem(VISITOR_KEY);
    if (s) {
      const v = JSON.parse(s) as Visitor;
      v.visitCount = (v.visitCount || 0) + 1;
      localStorage.setItem(VISITOR_KEY, JSON.stringify(v));
      return v;
    }
  } catch { /* ignore */ }
  const v: Visitor = {
    id: `visitor_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`,
    visitCount: 1,
  };
  try { localStorage.setItem(VISITOR_KEY, JSON.stringify(v)); } catch { /* ignore */ }
  return v;
}

function saveVisitor(v: Visitor) {
  try { localStorage.setItem(VISITOR_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

// Sync the captured lead to the Mothership -> CRM pipeline. Best-effort, never throws.
async function syncVisitor(v: Visitor): Promise<{ ok: boolean }> {
  if (!HYPERION_API_KEY) return { ok: false }; // no key yet -> keep locally, don't error
  try {
    const r = await fetch(`${API_BASE}/api/v1/hyperion/visitors`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-API-Key": HYPERION_API_KEY },
      body: JSON.stringify({
        visitor_id: v.id,
        name: v.name || null,
        email: v.email || null,
        mobile: v.mobile || null,
        can_contact: !!v.canContact,
        visit_count: v.visitCount || 1,
        domain: typeof window !== "undefined" ? window.location.hostname : "axoix.com",
      }),
    });
    return { ok: r.ok };
  } catch {
    return { ok: false };
  }
}

const LEAD_PROMPTS = {
  intro: "Happy to connect you with our team. May I get a few details? First — what's your name?",
  email: "Thanks! What's the best email to reach you?",
  mobile: "And a phone number for a quick callback? (optional — type 'skip')",
  consent: "Can our team contact you about AXOIX? (yes / no)",
  done: "🎉 Got it — our team will reach out shortly. Anything else I can help you with?",
  doneNoKey: "🎉 Thanks! I've noted your details and our team will follow up. Anything else I can help with?",
  badEmail: "That doesn't look like an email — mind re-entering it? (or type 'skip')",
};

// ============ BACKEND-DRIVEN ACTIONS (ported from hyperion-widget.js /voice) ============
interface VoiceResult {
  response_text?: string;
  action?: string;
  params?: Record<string, unknown>;
}

// Ask the Mothership to resolve a command-style message into an action (navigate, scroll, etc.).
// Multilingual + RAG-aware. Gated on the project API key; returns null to fall back to client nav.
async function sendVoiceCommand(command: string, language: string): Promise<VoiceResult | null> {
  if (!HYPERION_API_KEY) return null;
  try {
    const res = await fetch(`${API_BASE}/api/v1/hyperion/voice`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-API-Key": HYPERION_API_KEY },
      body: JSON.stringify({
        command,
        language,
        context: {
          domain: typeof window !== "undefined" ? window.location.hostname : "axoix.com",
          path: typeof window !== "undefined" ? window.location.pathname : "/",
          mode: "assistant",
        },
      }),
    });
    if (!res.ok) return null;
    return (await res.json()) as VoiceResult;
  } catch {
    return null;
  }
}

// ============ AUTO-RAG CONTENT REPORTER (ported from hyperion-widget.js) ============
// Hashes each page's visible content and reports changes to the Mothership so the RAG index
// self-updates from the live site. Gated on the project API key, deduped per-path via localStorage.
const CONTENT_HASH_KEY = "axoix_content_hashes";

const ContentReporter = {
  reportedHashes: {} as Record<string, string>,
  loaded: false,

  load() {
    if (this.loaded) return;
    try {
      this.reportedHashes = JSON.parse(localStorage.getItem(CONTENT_HASH_KEY) || "{}");
    } catch {
      this.reportedHashes = {};
    }
    this.loaded = true;
  },

  hash(content: string): string {
    let h = 0;
    const s = content.substring(0, 10000);
    for (let i = 0; i < s.length; i++) {
      h = (h << 5) - h + s.charCodeAt(i);
      h = h & h;
    }
    return Math.abs(h).toString(16);
  },

  extract(): string {
    const selectors = ["main", "article", ".main-content", "[role='main']", "#root"];
    let content = "";
    for (const sel of selectors) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el) {
        content = el.innerText;
        break;
      }
    }
    if (!content) content = document.body.innerText;
    return content.replace(/\s+/g, " ").trim();
  },

  async reportIfChanged() {
    if (!HYPERION_API_KEY || typeof window === "undefined") return;
    this.load();
    try {
      const path = window.location.pathname;
      const content = this.extract();
      if (content.length < 200) return; // skip near-empty / pre-hydration
      const h = this.hash(content);
      if (this.reportedHashes[path] === h) return;

      const res = await fetch(`${API_BASE}/api/v1/hyperion/content/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": HYPERION_API_KEY },
        body: JSON.stringify({
          site: window.location.hostname,
          path,
          title: document.title,
          hash: h,
          content_preview: content.substring(0, 500),
          full_url: window.location.href,
        }),
      });
      if (res.ok) {
        this.reportedHashes[path] = h;
        try {
          localStorage.setItem(CONTENT_HASH_KEY, JSON.stringify(this.reportedHashes));
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* silent fail — never disrupt the page */
    }
  },
};


interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface AxoixStatus {
  status: string;
  service: string;
  version: string;
  provider: string;
  characters: number;
  commands: number;
  languages: number;
  themes: number;
}

// Declare SpeechRecognition types for TypeScript
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onerror: ((this: SpeechRecognition, ev: Event) => void) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

// Offline knowledge base for AXOIX (from en.json)
const offlineKnowledge: Record<string, string> = {
  whatIs: `AXOIX is an intelligent Business Operating System (iBOS) — one AI-run platform that replaces your whole software stack instead of adding one more app.

**What it does:**
Instead of paying for, logging into, and reconciling 5–10 separate tools — a CRM, Tally for books, WhatsApp, a payroll sheet, a payment gateway — AXOIX runs the entire business from one dashboard: sales, accounting, HR, phone, messaging, and compliance, voice-controlled in 81 languages, with GST-ready books and zero-fee UPI built in.

**Key Capabilities:**
- **One platform, 22 modules** - CRM, Accounting, HR, Phone (LINK), Chat & Video, Social Studio (CHORUS), E-Sign (SEAL), Complaints (REMEDY), Projects, Inventory and more
- **22 AI characters** - HYPERION commands the crew: LEDGER closes the GST-ready books, LINK runs the phone, KEEPER handles HR, CHORUS catches leads in your DMs
- **Voice control in 81 languages** (22 Indian + 59 global) - say "बिक्री दिखाओ" and your sales open
- **19 industry verticals** - Hotel, Restaurant, Travel, Healthcare, Education, Logistics, Real Estate, Retail, Manufacturing, Construction, Consulting, IT Services, Trading, Car Dealer, Bike Dealer, Salon & Spa, Gym & Fitness, Arena Gaming, Study Abroad — each with its own module + public storefront
- **Built for India** - GST/TDS native, UPI collection with zero gateway fees on every invoice, Razorpay & Tally
- **Your data, your servers** - runs on AXOIX's own in-India infrastructure with the in-house HYPERION AI model
- **LINK Business Phone System** - FREE internal PBX with all paid plans, browser-based calling, no hardware needed
- **SEAL E-Signatures** - Get documents signed in minutes (FREE for all users)
- **REMEDY Complaints** - Track and resolve customer complaints with SLA monitoring (FREE for all users)
- **CHORUS Studio 2.0** - AI content pipeline: blogs, social posts, podcasts, videos, 14-platform distribution
- **WhatsApp CRM Inbox** - Multi-channel shared inbox (WhatsApp, Messenger, Instagram, Telegram, SMS) with SLA, AI auto-replies, drip sequences
- **Shopify Connector** - Sync products, orders, customers. Cart recovery and WhatsApp order notifications
- **Shiprocket** - 25+ courier partners, AWB generation, real-time tracking`,

  // FALLBACK ONLY. `pricing` here is the last resort: renderPricingAnswer()
  // below replaces it with live figures from GET /api/billing/pricing as soon
  // as they load, and matchCuratedAnswer() prefers those.
  //
  // These numbers were wrong for both price AND limits until 2026-09-10 —
  // Officer quoted at ₹4,999 against ₹1,500 in pricing_tiers, Commander
  // ₹14,999 against ₹3,600 — because the 2026-09-08 repricing had no way to
  // reach a hardcoded string. Keep them roughly current anyway: this is what a
  // visitor sees if the API is unreachable, and a wrong price is worse than a
  // vague one.
  pricing: `**AXOIX Plans** — one platform, one price, replaces 5–10 tools:

**Explorer — Free Forever**
- Core dashboard, 1 company, 1 vertical, up to 5 employees
- AXOIX AI assistant, SEAL e-signatures, REMEDY complaints
- Always free, no credit card

**Officer — ₹1,500/month**
- Everything in Explorer, plus up to 20 employees, 2 companies, 2 verticals
- Full 22 AI characters + voice in 81 languages
- Email Suite, LINK phone system, CHORUS social, API access

**Commander — ₹3,600/month**
- Everything in Officer, plus up to 50 employees, 6 companies, 5 verticals
- ALL specialty add-ons included, white-label, priority support

**Titan — Enterprise**
- Up to 100 employees, 10 companies, 10 verticals, dedicated support, custom SLA

Prices exclude 18% GST. SEAL e-signatures and REMEDY complaints are free on every plan, including Explorer.`,

  link: `**LINK - Your Built-in Business Phone System**

Every AXOIX subscription includes a complete business phone system. No expensive PBX hardware, no complicated setup, no monthly fees for internal calls.

**FREE with Every Paid Plan:**
- Internal extensions: 3 / 10 / 15 / unlimited by plan
- Browser-based WebRTC calling
- Internal voicemail
- Call transfers between team members
- Presence/availability status
- No phone hardware required

**Premium LINK Features (Addon):**
- DID phone numbers (+91-XXX-XXXX)
- Professional IVR builder
- Call recording & storage
- Click-to-call from CRM
- Missed call alerts
- Call analytics & reports

**How LINK Works:**
LINK runs on enterprise-grade infrastructure. Your team gets softphone capability right in their browser — no apps to install, no phones to buy. Just open your browser and start calling.`,

  features: `**AXOIX Features:**

**AI Costing Engine (Game Changer):**
Describe your project or upload a wiring diagram — get instant cost estimates with complete BOQ. Works for electrical, construction, HVAC, manufacturing, IT and 12+ industries.

**The AI crew (named workers):**
- HYPERION - Commander, central AI + voice
- LEDGER - Accounting & GST-ready books
- KEEPER - HR & payroll
- LINK - Business phone system (PBX)
- FORGE - Project management
- HAUL - Inventory
- CIPHER - Tax & compliance
- BARTER - Sales & quotes (CRM)
- PROBE - Tenders / NIT extraction
- ECHO - Email
- CHORUS - Social media & content studio
- VOYAGE - Travel & tourism

**Voice Command System:**
Natural voice control in 81 languages (22 Indian + 59 global). Say "Call John" and LINK dials. Say "Show sales report" and your dashboard opens. Phonetic fuzzy matching understands accents, dialects, and background noise.

**19 Industry Verticals with Public Storefronts:**
Hotel, Restaurant, Travel & Tourism, Healthcare, Education, Logistics, Real Estate, Retail, Manufacturing, Construction, Professional Services, Trading, Car Dealer, Bike Dealer, Salon & Spa, Gym & Fitness, Arena Gaming, Study Abroad.

**NEW — WhatsApp CRM Inbox:**
Multi-channel shared inbox for WhatsApp, Facebook Messenger, Instagram DM, Telegram, SMS. SLA tracking, AI auto-replies, drip sequences, agent assignment.

**NEW — Shopify & E-Commerce Connectors:**
Connect Shopify stores, Shiprocket (25+ couriers), IndiaMART leads. Cart recovery, order notifications via WhatsApp.

**NEW — CHORUS Studio 2.0:**
AI content pipeline: blog articles, social posts, podcasts, videos. Voice Studio with 5 TTS providers. Video Studio with AI generation.

**Included FREE:**
- SEAL E-Signatures - Digital signatures for quotes, contracts
- REMEDY Complaints - Track complaints with SLA monitoring
- CHORUS Social Media - Schedule posts, analytics, unified inbox`,

  contact: `**Get in Touch with AXOIX:**

**Website:** https://axoix.com

**Email:**
- Support: support@axoix.com
- Sales: sales@axoix.com

**Office:** New Delhi, India

**Get Started:**
1. Visit axoix.com
2. Click "Join Now"
3. Start with the free Explorer plan
4. Upgrade when ready

We support businesses worldwide in 81 languages (22 Indian + 59 Global)!`,

  default: `I'm HYPERION, the AI assistant for AXOIX!

**AXOIX is an intelligent Business Operating System** — one AI-run platform that replaces your whole software stack (CRM, books, HR, phone, messaging, compliance) instead of adding one more app.

- **22 AI characters** running concrete business jobs
- **22 modules · 19 industry verticals · 81 languages** with voice
- **GST-ready books + zero-fee UPI** built for India
- **LINK phone system** - free internal calling with all paid plans
- **SEAL e-signatures & REMEDY complaints** - free on every plan

**Ask me about:**
- "What is AXOIX?" - Full platform overview
- "Pricing" - Plans from free Explorer to enterprise Titan
- "LINK phone system" - Business phone features
- "Features" - AI characters and capabilities
- "How to get started" - Contact and signup info

What would you like to know?`
};

// Curated answer for a query, or null when no topic matches. Split out from
// getOfflineResponse() so a caller can tell "we know this one" apart from
// "fall back to the generic menu" — chatWithAxoix() needs that distinction.
/**
 * Live pricing answer, built from `pricing_tiers` via the public pricing API.
 *
 * The chat quotes prices to prospects, so it must not carry its own copy of
 * them — that is how Officer came to be advertised at 3.3x what the checkout
 * charges. This caches the rendered answer at module level; matchCuratedAnswer()
 * uses it when present and falls back to offlineKnowledge.pricing when the API
 * has not answered (or failed), so the chat never blocks on the network.
 */
let livePricingAnswer: string | null = null;

function formatRupees(value: number): string {
  // Indian digit grouping: 1,50,000 not 150,000.
  return "\u20b9" + Math.round(value).toLocaleString("en-IN");
}

function renderPricingAnswer(tiers: PricingTierAPI[]): string | null {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;

  const ordered = [...tiers].sort(
    (a, b) => (a.tierLevel ?? 0) - (b.tierLevel ?? 0)
  );

  const blocks = ordered.map((t) => {
    const monthly = t.monthlyPrice;
    const price =
      typeof monthly === "number" && monthly > 0
        ? `${formatRupees(monthly)}/month`
        : "Free Forever";

    const limits = (t.featureLimits || {}) as Record<string, unknown>;
    const bits: string[] = [];
    ([
      ["verticals", "vertical", "verticals"],
      ["users", "user", "users"],
      ["employees", "employee", "employees"],
      ["companies", "company", "companies"],
    ] as const).forEach(([key, one, many]) => {
      const n = limits[key];
      if (typeof n !== "number") return;
      bits.push(n === -1 ? `unlimited ${many}` : `${n} ${n === 1 ? one : many}`);
    });

    const detail = bits.length ? `\n- Up to ${bits.join(", ")}` : "";
    return `**${t.name} \u2014 ${price}**${detail}`;
  });

  return (
    "**AXOIX Plans** \u2014 one platform, one price, replaces 5\u201310 tools:\n\n" +
    blocks.join("\n\n") +
    "\n\nPrices exclude 18% GST. SEAL e-signatures and REMEDY complaints are " +
    "free on every plan, including Explorer."
  );
}

// Fire once at module load. Failure is not an error path worth surfacing: the
// static fallback below is already a reasonable answer.
fetchPricingData()
  .then((data) => {
    if (data?.tiers) livePricingAnswer = renderPricingAnswer(data.tiers);
  })
  .catch(() => {
    /* keep the fallback */
  });

function matchCuratedAnswer(message: string): string | null {
  const lowerMsg = message.toLowerCase();

  if (lowerMsg.includes("what is") || lowerMsg.includes("about") || lowerMsg.includes("axoix") || lowerMsg.includes("tell me")) {
    return offlineKnowledge.whatIs;
  }
  if (lowerMsg.includes("price") || lowerMsg.includes("pricing") || lowerMsg.includes("cost") || lowerMsg.includes("plan") || lowerMsg.includes("₹") || lowerMsg.includes("rupee")) {
    return livePricingAnswer ?? offlineKnowledge.pricing;
  }
  if (lowerMsg.includes("link") || lowerMsg.includes("phone") || lowerMsg.includes("call") || lowerMsg.includes("voip")) {
    return offlineKnowledge.link;
  }
  if (lowerMsg.includes("feature") || lowerMsg.includes("character") || lowerMsg.includes("voice") || lowerMsg.includes("module") || lowerMsg.includes("industry")) {
    return offlineKnowledge.features;
  }
  if (lowerMsg.includes("contact") || lowerMsg.includes("email") || lowerMsg.includes("start") || lowerMsg.includes("trial") || lowerMsg.includes("sign up")) {
    return offlineKnowledge.contact;
  }

  return null;
}

// Get offline response based on user query. Unchanged behaviour: a curated
// answer when one matches, otherwise the "ask me about" menu.
function getOfflineResponse(message: string): string {
  return matchCuratedAnswer(message) ?? offlineKnowledge.default;
}

// Chat with AXOIX. The live model answers every question when it's on and
// reachable; curated answers are the degraded-mode fallback, not a filter in
// front of it — matchCuratedAnswer() matches on bare substrings ("axoix",
// "about", "start"...) and was intercepting most real questions before the
// AI, verified working since BR-028, ever saw them.
async function chatWithAxoix(message: string, language: string): Promise<string> {
  // Kept as a kill switch. Off -> go straight to curated/offline, no network call.
  if (!LIVE_MODEL_ENABLED) return getOfflineResponse(message);

  try {
    const response = await fetch(AXOIX_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Picks the RAG knowledge base on the mothership side. Our own sites are
        // not tenants — they authenticate as `platform` (BR-028). An unknown
        // value falls back to axoix.in rather than erroring, so a typo here
        // degrades to the wrong knowledge base, not a dead widget.
        "X-AXOIX-Site": "axoix.com",
      },
      body: JSON.stringify({
        message,
        character: "HYPERION",
        language: language,
        context: `user is on marketing website axoix.com, preferred language: ${language}`,
      }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.response || data.message || "I received your message but couldn't process it properly.";
  } catch (error) {
    console.error("AXOIX API error:", error);
    // Return offline response instead of throwing
    return getOfflineResponse(message);
  }
}

// Check HYPERION AI status (public endpoint)
async function getAxoixStatus(): Promise<AxoixStatus | null> {
  try {
    const response = await fetch(AXOIX_STATUS_URL);
    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch {
    return null;
  }
}

const AxoixChat: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [apiStatus, setApiStatus] = useState<"online" | "offline" | "checking">("checking");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const synthRef = useRef<SpeechSynthesis | null>(null);
  // Lead-capture: visitor memory + current step (refs avoid stale closures in the memoized handlers)
  const visitorRef = useRef<Visitor | null>(null);
  const leadStepRef = useRef<null | "name" | "email" | "mobile" | "consent">(null);
  useEffect(() => { visitorRef.current = loadVisitor(); }, []);

  // Smooth-scroll to a selector with a brief highlight (ported from widget ActionExecutor)
  const scrollToSelector = useCallback((selector: string): boolean => {
    if (!selector) return false;
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return false;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.style.transition = "box-shadow 0.3s ease";
    el.style.boxShadow = "0 0 0 4px rgba(124, 58, 237, 0.45)";
    setTimeout(() => {
      el.style.boxShadow = "";
    }, 2000);
    return true;
  }, []);

  // Navigation handler (client-side site map)
  const handleNavigation = useCallback((route: SiteRoute) => {
    if (route.section) {
      // Scroll to section on homepage (highlight on arrival)
      navigate("/");
      setTimeout(() => scrollToSelector(`#${route.section}`), 300);
    } else {
      // Navigate to page
      navigate(route.path);
    }
    // Close chat after navigation
    setIsOpen(false);
  }, [navigate, scrollToSelector]);

  // Execute a backend-driven action (from /voice). Ported from widget ActionExecutor.
  const executeAction = useCallback(
    (action: string, params: Record<string, unknown> = {}): boolean => {
      const path = (params.path || params.url) as string | undefined;
      switch (action) {
        case "navigate":
          if (!path) return false;
          if (/^https?:\/\//.test(path)) {
            window.open(path, "_blank");
          } else {
            navigate(path);
            setIsOpen(false);
          }
          return true;
        case "scroll_to":
          return scrollToSelector(
            (params.selector as string) || (params.section ? `#${params.section}` : "")
          );
        case "open_external":
          if (params.url) {
            window.open(params.url as string, "_blank");
            return true;
          }
          return false;
        default:
          return false;
      }
    },
    [navigate, scrollToSelector]
  );

  // Check API status on mount
  useEffect(() => {
    getAxoixStatus().then((status) => {
      setApiStatus(status?.status === "online" ? "online" : "offline");
    });
  }, []);

  // Auto-RAG: report page content to the Mothership on each route change (debounced for
  // dynamic/prerendered content). No-op until the project API key is configured.
  useEffect(() => {
    const timer = setTimeout(() => ContentReporter.reportIfChanged(), 2500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Initialize speech synthesis
  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      synthRef.current = window.speechSynthesis;
    }
  }, []);

  // Map i18n language codes to BCP 47 language tags for speech recognition
  const getSpeechLang = useCallback((lang: string): string => {
    const langMap: Record<string, string> = {
      en: "en-US",
      hi: "hi-IN",
      es: "es-ES",
      fr: "fr-FR",
      de: "de-DE",
      pt: "pt-BR",
      zh: "zh-CN",
      ja: "ja-JP",
      ko: "ko-KR",
      ar: "ar-SA",
      ru: "ru-RU",
    };
    return langMap[lang] || langMap[lang.split("-")[0]] || "en-US";
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== "undefined") {
      const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognitionAPI) {
        const recognition = new SpeechRecognitionAPI();
        recognition.continuous = false;
        recognition.interimResults = true; // Enable real-time transcription
        recognition.lang = getSpeechLang(i18n.language);

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          // Build transcript from all results
          let finalTranscript = "";
          let interimTranscript = "";

          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              finalTranscript += result[0].transcript;
            } else {
              interimTranscript += result[0].transcript;
            }
          }

          // Show real-time text as user speaks
          setInputValue(finalTranscript || interimTranscript);

          // Stop listening when we have a final result
          if (finalTranscript) {
            setIsListening(false);
          }
        };

        recognition.onerror = () => {
          setIsListening(false);
        };

        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
      }
    }
  }, [i18n.language, getSpeechLang]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Generate welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: "welcome",
        role: "assistant",
        content: t("chat.welcome"),
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);

      // Speak welcome message if voice is enabled
      if (voiceEnabled && synthRef.current) {
        speakText(t("chat.welcomeVoice"));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, messages.length, voiceEnabled, t]);

  // Text-to-speech function
  const speakText = useCallback(
    (text: string) => {
      if (!synthRef.current || !voiceEnabled) return;

      // Cancel any ongoing speech
      synthRef.current.cancel();

      // Step 1: Strip formatting that interferes with number detection
      const preClean = text
        .replace(/^\s*\d+\.\s*/gm, "")           // Remove numbered list markers (1. 2. 3.) BEFORE number conversion
        .replace(/^\s*[-*•]\s*/gm, "");           // Remove bullet points (-, *, •) at start of lines

      // Step 2: Convert numbers to words (before + signs are stripped)
      const numbersConverted = convertNumbersForSpeech(preClean);

      // Step 3: Clean remaining formatting for speech
      const speechText = numbersConverted
        // Brand names, spelled phonetically for SpeechSynthesis. Left as-is the
        // engine reads "AXOIX" as "axoya" — the -OIX ending gets treated as a
        // French-style digraph. Same class of problem as numberToWords above.
        // Must run BEFORE the other replacements so nothing has split the word.
        // "Ax-oh-ix" is the respelling poc.axoix.io's useVoiceSynthesis.js
        // already uses and has verified correct — matched here rather than
        // guessing a second one for this widget. "ak-soyks" was a first
        // attempt here that fixed the French digraph but landed on a
        // different wrong sound.
        .replace(/\bAXOIX\b/gi, "Ax-oh-ix")
        .replace(/\*\*/g, "")                    // Remove bold markers **text**
        .replace(/\n{2,}/g, ". ")                // Double newlines become periods
        .replace(/\n/g, ", ")                    // Single newlines become commas
        .replace(/[()]/g, "")                    // Remove parentheses
        .replace(/×/g, " times ")                // Multiplication sign to word
        .replace(/—/g, " - ")                    // Em dash to regular dash
        .replace(/[🚀🎉📞📧📊💰📋👥🔒🆓⭐👋🙏🌍🏢📬1️⃣2️⃣3️⃣4️⃣5️⃣🤖🗣️💡✅❌⚡🔥💪🎯📈🏨✈️🍽️🎓🏥🚛🏠🔄]/g, "") // Remove emojis
        .replace(/:\s*$/gm, ".")                 // Colons at end of line become periods
        .replace(/\s{2,}/g, " ")                 // Multiple spaces to single
        .replace(/,\s*,/g, ",")                  // Remove double commas
        .replace(/\.\s*\./g, ".")                // Remove double periods
        .trim();

      const utterance = new SpeechSynthesisUtterance(speechText);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      // Try to get a voice matching user's language
      const speechLang = getSpeechLang(i18n.language);
      const langPrefix = speechLang.split("-")[0];
      const voices = synthRef.current.getVoices();
      const preferredVoice = voices.find(
        (v) =>
          v.lang.startsWith(langPrefix) &&
          (v.name.includes("Google") || v.name.includes("Microsoft"))
      ) || voices.find((v) => v.lang.startsWith(langPrefix));

      if (preferredVoice) {
        utterance.voice = preferredVoice;
        utterance.lang = preferredVoice.lang;
      } else {
        utterance.lang = speechLang;
      }

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      synthRef.current.speak(utterance);
    },
    [voiceEnabled, i18n.language, getSpeechLang]
  );

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  }, []);

  // ============ LEAD-CAPTURE CONVERSATION ============
  const startLeadFlow = useCallback(() => {
    leadStepRef.current = "name";
    const m: Message = { id: `lead-${Date.now()}`, role: "assistant", content: LEAD_PROMPTS.intro, timestamp: new Date() };
    setMessages((prev) => [...prev, m]);
    if (voiceEnabled) speakText(LEAD_PROMPTS.intro);
  }, [voiceEnabled, speakText]);

  const handleLeadStep = useCallback(async (text: string) => {
    const v = visitorRef.current || loadVisitor();
    visitorRef.current = v;
    const val = text.trim();
    const lower = val.toLowerCase();
    const skip = ["skip", "no", "later", "none", "nope"].includes(lower);
    const step = leadStepRef.current;
    let reply = "";

    if (step === "name") {
      if (val.length > 1 && !skip) v.name = val;
      leadStepRef.current = "email";
      reply = LEAD_PROMPTS.email;
    } else if (step === "email") {
      if (val.includes("@") && val.includes(".")) {
        v.email = val;
        leadStepRef.current = "mobile";
        reply = LEAD_PROMPTS.mobile;
      } else if (skip) {
        leadStepRef.current = "mobile";
        reply = LEAD_PROMPTS.mobile;
      } else {
        reply = LEAD_PROMPTS.badEmail;
      }
    } else if (step === "mobile") {
      if (!skip && val.replace(/\D/g, "").length >= 8) v.mobile = val;
      leadStepRef.current = "consent";
      reply = LEAD_PROMPTS.consent;
    } else if (step === "consent") {
      v.canContact = ["yes", "sure", "ok", "okay", "y", "yep", "haan", "ha"].includes(lower);
      v.leadSent = true;
      leadStepRef.current = null;
      saveVisitor(v);
      const res = await syncVisitor(v);
      reply = res.ok ? LEAD_PROMPTS.done : LEAD_PROMPTS.doneNoKey;
    }

    saveVisitor(v);
    const m: Message = { id: `lead-${Date.now()}`, role: "assistant", content: reply, timestamp: new Date() };
    setMessages((prev) => [...prev, m]);
    if (voiceEnabled) speakText(reply);
  }, [voiceEnabled, speakText]);

  // Start listening
  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Speech recognition error:", error);
      }
    }
  }, [isListening]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, [isListening]);

  // Send message handler
  const handleSendMessage = useCallback(
    async (messageText?: string) => {
      const text = messageText || inputValue;
      if (!text.trim() || isLoading) return;

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: "user",
        content: text.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setIsLoading(true);

      // Lead-capture flow takes priority over AI + navigation
      if (leadStepRef.current) {
        setIsLoading(false);
        await handleLeadStep(text.trim());
        return;
      }
      if (visitorRef.current && !visitorRef.current.leadSent) {
        const l = text.toLowerCase();
        const leadTriggers = ["talk to sales", "contact sales", "speak to sales", "speak to someone", "call me", "callback", "call back", "book a demo", "get a demo", "talk to a human", "sales team", "get in touch", "request a quote", "request a call"];
        if (leadTriggers.some((tr) => l.includes(tr))) {
          setIsLoading(false);
          startLeadFlow();
          return;
        }
      }

      try {
        // Advanced: with a backend key, let HYPERION resolve command-style inputs into precise
        // actions (multilingual, RAG-aware). Falls back to the client-side site map otherwise.
        const looksLikeCommand =
          /^(go to|open|show me|take me|navigate|scroll|visit|jump to|call |dial )/i.test(text.trim()) ||
          /(दिखाओ|खोलो|जाओ|ले चलो|दिखाइए)/.test(text);
        if (HYPERION_API_KEY && looksLikeCommand) {
          const vr = await sendVoiceCommand(text.trim(), i18n.language);
          if (vr && (vr.response_text || vr.action)) {
            const msg = vr.response_text || "On it.";
            setMessages((prev) => [
              ...prev,
              { id: `assistant-${Date.now()}`, role: "assistant", content: msg, timestamp: new Date() },
            ]);
            if (voiceEnabled) speakText(msg);
            if (vr.action && vr.action !== "speak" && vr.action !== "error") {
              setTimeout(() => executeAction(vr.action!, vr.params || {}), 600);
            }
            setIsLoading(false);
            return;
          }
        }

        // Check for navigation intent FIRST
        const navIntent = detectNavigationIntent(text.trim());

        // If strong navigation intent detected, navigate immediately
        if (navIntent) {
          const navMessage = `Taking you to ${navIntent.name}...`;

          const assistantMessage: Message = {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            content: navMessage,
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, assistantMessage]);

          // Speak and navigate
          if (voiceEnabled) {
            speakText(navMessage);
          }

          // Auto-navigate after a brief delay
          setTimeout(() => {
            handleNavigation(navIntent);
          }, 800);

          setIsLoading(false);
          return;
        }

        // No navigation intent - call the AXOIX API
        const responseText = await chatWithAxoix(text.trim(), i18n.language);

        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: responseText,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Speak the response
        if (voiceEnabled) {
          speakText(responseText);
        }
      } catch (error) {
        // Fallback message on API error
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: t("chat.errorMessage"),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue, isLoading, voiceEnabled, speakText, i18n.language, t]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { key: "whatIs", label: t("chat.quickActions.whatIs") },
    { key: "pricing", label: t("chat.quickActions.pricing") },
    { key: "link", label: t("chat.quickActions.link") },
    { key: "sales", label: "📞 Talk to sales" },
  ];

  const handleQuickAction = (action: string) => {
    setInputValue(action);
    setTimeout(() => {
      handleSendMessage(action);
    }, 100);
  };

  // Format message content with markdown
  const formatMessage = (content: string) => {
    return content.split("\n").map((line, i) => (
      <span key={i}>
        {line.split("**").map((part, j) => (j % 2 === 1 ? <strong key={j}>{part}</strong> : part))}
        {i < content.split("\n").length - 1 && <br />}
      </span>
    ));
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        className={`axoix-chat-toggle ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? t("chat.closeChat") : t("chat.openChat")}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <div className="axoix-chat-avatar">
            <img src={axoixAvatar} alt="HYPERION" />
          </div>
        )}
        {!isOpen && <span className="axoix-chat-badge">AI</span>}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="axoix-chat-window">
          {/* Header */}
          <div className="axoix-chat-header">
            <div className="axoix-chat-header-info">
              <img src={axoixAvatar} alt="HYPERION" className="axoix-chat-header-avatar" />
              <div>
                <h4>HYPERION</h4>
                <span className="axoix-status">
                  <span
                    className={`axoix-status-dot ${isSpeaking ? "speaking" : ""} ${
                      apiStatus === "offline" ? "offline" : ""
                    }`}
                  ></span>
                  {isSpeaking
                    ? t("chat.speaking")
                    : isListening
                    ? t("chat.listening")
                    : apiStatus === "checking"
                    ? t("chat.connecting")
                    : apiStatus === "online"
                    ? t("chat.online")
                    : t("chat.offline")}
                </span>
              </div>
            </div>
            <div className="axoix-chat-header-actions">
              {/* Voice Toggle */}
              <button
                className={`axoix-voice-toggle ${voiceEnabled ? "active" : ""}`}
                onClick={() => {
                  setVoiceEnabled(!voiceEnabled);
                  if (isSpeaking) stopSpeaking();
                }}
                title={voiceEnabled ? t("chat.muteVoice") : t("chat.enableVoice")}
              >
                {voiceEnabled ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <line x1="23" y1="9" x2="17" y2="15"></line>
                    <line x1="17" y1="9" x2="23" y2="15"></line>
                  </svg>
                )}
              </button>
              <button
                className="axoix-chat-close"
                onClick={() => {
                  setIsOpen(false);
                  stopSpeaking();
                  stopListening();
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages.
              data-lenis-prevent is load-bearing: the site runs Lenis smooth
              scroll (<html class="lenis">), which captures wheel events at the
              document level. Without this opt-out the wheel scrolls the PAGE
              behind the widget and this list never moves, even though it has
              overflow-y: auto. Scoped to the scroller so the rest of the page
              keeps smooth scrolling — unlike TermsAcceptanceModal, which stops
              Lenis entirely because it is a full-screen blocking modal. */}
          <div className="axoix-chat-messages" data-lenis-prevent>
            {messages.map((message) => (
              <div key={message.id} className={`axoix-message ${message.role}`}>
                {message.role === "assistant" && (
                  <img src={axoixAvatar} alt="HYPERION" className="axoix-message-avatar" />
                )}
                <div className="axoix-message-content">
                  <p>{formatMessage(message.content)}</p>
                  <span className="axoix-message-time">
                    {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="axoix-message assistant">
                <img src={axoixAvatar} alt="HYPERION" className="axoix-message-avatar" />
                <div className="axoix-message-content">
                  <div className="axoix-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && (
            <div className="axoix-quick-actions">
              {quickActions.map((action) => (
                <button key={action.key} onClick={() => handleQuickAction(action.label)}>
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="axoix-chat-input">
            {/* Microphone button */}
            <button
              className={`axoix-mic-btn ${isListening ? "listening" : ""}`}
              onClick={isListening ? stopListening : startListening}
              disabled={isLoading || !recognitionRef.current}
              title={isListening ? t("chat.stopListening") : t("chat.startVoice")}
            >
              {isListening ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </svg>
              )}
            </button>

            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? t("chat.placeholderListening") : t("chat.placeholder")}
              disabled={isLoading || isListening}
            />

            <button
              className="axoix-send-btn"
              onClick={() => handleSendMessage()}
              disabled={isLoading || !inputValue.trim()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>

          {/* Footer */}
          <div className="axoix-chat-footer">
            <span>🎙️ {t("chat.voiceEnabled")}</span> • {t("chat.poweredBy")}
          </div>
        </div>
      )}
    </>
  );
};

export default AxoixChat;
